import {Button, Dropdown, Icon, Text} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {DiscountReward, PromotionCondition, rewardTypeToIdMap} from '@/types/promotion'
import {COLOR_CODE} from '@/utils/constants/colors'
import {Constants} from '@/utils/constants/commonConstants'
import {RewardTypes} from '@/utils/constants/promotionConstants'
import {FC} from 'react'
import {RewardDiscount} from './RewardDiscount'
import {RewardFreeProducts} from './RewardFreeProducts'
import {RewardGiftItem} from './RewardGiftItem'
import {RewardPoints} from './RewardPoints'

export type RewardItemProps = {
  promotionCondition: PromotionCondition
  index: number
}

export const RewardItem: FC<RewardItemProps> = ({promotionCondition, index}) => {
  const {promotion, setMaxDiscountModalVisible, onResultChange, promotionConfig, updatePromotion} = usePromotion()
  const {maxDiscountPercentage, conditions} = promotionConfig
  const rewardOptions = conditions?.rewardTypeOptions?.map(option => ({
    displayText: option.label,
    id: option.value,
    discountType: option.discountType || option.rewardType,
    rewardType: option.rewardType,
  }))

  const selectedReward = promotion?.promotionConditions?.[0]?.rewards?.[0]
  const rewardType =
    rewardTypeToIdMap?.[selectedReward?.type || '']?.[selectedReward?.discountType || ''] || selectedReward?.type || ''
  const rewardValue = rewardOptions?.filter(item => item.id === rewardType)

  const handleDeleteLayer = sequence => {
    const updatedConditions = promotion?.promotionConditions?.filter(condition => condition.sequence !== sequence)
    onResultChange({promotionConditions: updatedConditions})
  }

  const handleSelectedRewardItem = (value: any) => {
    if (!value || !value.length) return
    const selected = value[0]

    updatePromotion((prev: any) => ({
      ...prev,
      promotionConditions: prev?.promotionConditions?.map(condition => ({
        ...condition,
        rewards: [
          {
            type: selected.rewardType,
            items: [],
            discountType: selected.discountType,
          },
        ],
      })),
    }))
  }

  const isRewardTypePercentDiscount = rewardType === RewardTypes.PERCENTAGE_DISCOUNT

  const discountPercentage = (promotionCondition?.rewards?.[0] as DiscountReward)?.discountValue || ''

  const isDiscountPerGthMxdiscount = discountPercentage > maxDiscountPercentage

  const getRewardType = () => {
    switch (rewardType) {
      case RewardTypes.FREE_PRODUCT:
        return <RewardFreeProducts index={index} promotionCondition={promotionCondition} />
      case RewardTypes.DISCOUNT:
        return <RewardDiscount index={index} promotionCondition={promotionCondition} showDynamicPlaceholder />
      case RewardTypes.GIFT_ITEM:
        return <RewardGiftItem index={index} promotionCondition={promotionCondition} />
      case RewardTypes.POINTS:
        return <RewardPoints index={index} promotionCondition={promotionCondition} />
      case RewardTypes.FREE_PRODUCT_FROM_LIST:
        return null
      case RewardTypes.PERCENTAGE_DISCOUNT:
        return (
          <RewardDiscount
            promotionCondition={promotionCondition}
            showDynamicPlaceholder
            discountType={rewardValue?.[0]?.discountType}
          />
        )
      case RewardTypes.CASH_DISCOUNT:
        return <RewardDiscount promotionCondition={promotionCondition} discountType={rewardValue?.[0]?.discountType} />
      default:
        return null
    }
  }

  const renderRewardType = () => {
    const rewardTypeComponent = getRewardType()

    if (rewardTypeComponent) {
      return (
        <>
          <Text>{','}</Text>
          {rewardTypeComponent}
        </>
      )
    } else {
      return null
    }
  }
  return (
    <>
      <Dropdown
        isJsonWrap={false}
        childList={rewardOptions}
        placeholder={Constants.Select}
        updateValue={handleSelectedRewardItem}
        isClearable={false}
        value={rewardValue}
        autocomplete="off"
        required={true}
        disabled={index > 0}
        selection="single"
      />
      {renderRewardType()}
      {promotionCondition?.sequence > 1 && (
        <Button
          uiSchema={{
            variant: 'tertiary',
            title: 'Delete layer',
            isDark: true,
            size: 'small',
            className: '',
            isLoading: false,
          }}
          handleButton={() => handleDeleteLayer(promotionCondition?.sequence)}
        />
      )}

      {isRewardTypePercentDiscount && isDiscountPerGthMxdiscount && (
        <>
          <Text style={{color: COLOR_CODE._D0D0D0, display: 'flex', gap: 3, alignItems: 'center'}} size="small">
            <Icon alt="alt value" icon="info" size="small" />
            Discount % exceeds max
          </Text>
          <Button
            handleButton={() => {
              setMaxDiscountModalVisible(true)
            }}
            uiSchema={{
              className: '',
              size: 'small',
              title: 'Edit Max',
              variant: 'tertiary',
              isLoading: false,
              isDark: true,
              iconLeading: 'mode_edit',
            }}
          />
        </>
      )}
    </>
  )
}
